import { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useBoolean, useIntersectionObserver } from "usehooks-ts";

import { Skeleton, TileBaseProps } from "@/components/ui-v2";
import { TileBase } from "@/components/ui-v2/Tiles/TileBase/TileBase";
import { getHasAudio } from "@/utils/video";

import { VideoPreviewTileControls } from "./VideoPreviewTileControls";

export type VideoPreviewTileAction =
  | "SELECT"
  | "DOUBLE_CLICK"
  | "OPEN_CONTEXT_MENU"
  | "TOGGLE_MUTE"
  | "TOGGLE_PLAY";

type VideoPreviewTileState = "loading" | "active" | "hidden";

export interface VideoPreviewTileProps extends TileBaseProps {
  src: string;
  isLazyloaded?: boolean;
  isSelectable?: boolean;
  shouldPlayOnHover?: boolean;
  shouldResetOnStop?: boolean;
  shouldPlayOnSelected?: boolean;
  onAction?: (event: React.MouseEvent, action: VideoPreviewTileAction) => void;
}

const getVideoPreviewTileState = (
  isReady: boolean,
  isLazyloaded: boolean,
  isIntersecting: boolean,
): VideoPreviewTileState => {
  if (isLazyloaded && !isIntersecting) {
    return "hidden";
  }

  if (!isReady) {
    return "loading";
  }

  return "active";
};

export const VideoPreviewTile = (props: VideoPreviewTileProps) => {
  const {
    src,
    isSelected = false,
    isSelectable = false,
    isLazyloaded = false,
    shouldPlayOnHover = false,
    shouldResetOnStop = false,
    shouldPlayOnSelected = false,
    slots = {},
    slotProps = {},
    onAction,
    ...rest
  } = props;
  const {
    value: isHovered,
    setTrue: hoverIn,
    setFalse: hoverOut,
  } = useBoolean(false);
  const { value: isReady, setTrue: readyVideo } = useBoolean(false);
  const { value: isMuted, toggle: toggleMuted } = useBoolean(true);
  const {
    value: isPlaying,
    toggle: togglePlaying,
    setTrue: play,
    setFalse: stop,
  } = useBoolean(false);
  const [hasAudio, setHasAudio] = useState(false);
  const playerRef = useRef<ReactPlayer>(null);

  const handleControlsAction = (
    e: React.MouseEvent,
    action: VideoPreviewTileAction,
  ) => {
    switch (action) {
      case "TOGGLE_MUTE":
        toggleMuted();
        break;
      case "TOGGLE_PLAY":
        togglePlaying();
        break;
      default:
        onAction?.(e, action);
    }
  };

  const handleReady = () => {
    readyVideo();

    const player = playerRef.current?.getInternalPlayer() as HTMLVideoElement;

    if (player) {
      setHasAudio(getHasAudio(player));
    }
  };

  const handleAction =
    (action: VideoPreviewTileAction) => (e: React.MouseEvent) => {
      onAction?.(e, action);
    };

  useEffect(() => {
    if (shouldResetOnStop && !isPlaying) {
      playerRef.current?.seekTo(0);
    }
  }, [shouldResetOnStop, isPlaying]);

  useEffect(() => {
    if (shouldPlayOnSelected) {
      if (isSelected) {
        play();
      } else {
        stop();
      }
    }
  }, [shouldPlayOnSelected, isSelected, play, stop]);

  useEffect(() => {
    if (shouldPlayOnHover) {
      if (isHovered) {
        play();
      } else {
        stop();
      }
    }
  }, [shouldPlayOnHover, isHovered, play, stop]);

  const { isIntersecting, ref } = useIntersectionObserver({
    rootMargin: "5%",
    threshold: 0.1,
    freezeOnceVisible: true,
  });

  const videoPreviewTileState: VideoPreviewTileState = getVideoPreviewTileState(
    isReady,
    isLazyloaded,
    isIntersecting,
  );

  return (
    <TileBase
      ref={ref}
      isSelected={isSelected}
      isSelectable={isSelectable}
      onClick={handleAction("SELECT")}
      onDoubleClick={handleAction("DOUBLE_CLICK")}
      onContextMenu={handleAction("OPEN_CONTEXT_MENU")}
      onMouseEnter={shouldPlayOnHover ? hoverIn : undefined}
      onMouseLeave={shouldPlayOnHover ? hoverOut : undefined}
      slots={{
        topRightEl: VideoPreviewTileControls,
        ...slots,
      }}
      slotProps={{
        topRightEl: {
          isPlaying,
          isMuted,
          disabledMuteButton: !hasAudio,
          onAction: handleControlsAction,
        },
        ...slotProps,
      }}
      sx={{
        "& video": {
          objectFit: "cover",
        },
      }}
      {...rest}
    >
      {["loading", "hidden"].includes(videoPreviewTileState) && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        />
      )}
      {["loading", "active"].includes(videoPreviewTileState) && (
        <ReactPlayer
          loop
          muted={isMuted}
          playing={isPlaying}
          ref={playerRef}
          url={src}
          controls={false}
          width="100%"
          height="100%"
          config={{
            file: {
              attributes: {
                preload: "metadata",
              },
            },
          }}
          onReady={handleReady}
          style={{
            display: videoPreviewTileState === "active" ? "block" : "none",
          }}
        />
      )}
    </TileBase>
  );
};
